/**
 * Shared helpers for the historical dashboards (INSIGHT-HIST-12 onwards).
 */

/**
 * Milliseconds as a duration people read at a glance: 793624 -> "13m 14s".
 *
 * Returns "No Data" for null or undefined. A real zero prints as "0s", because
 * missing data and a genuine zero must never look the same.
 */
export const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) {
    return "No Data";
  }

  const totalSeconds = Math.round(Number(ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
};

/** A percentage with one decimal, or "No Data" when it is unknown. */
export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "No Data";
  }

  return `${Number(value).toFixed(1)}%`;
};

/** A timestamp as local time, or "-" when there is none. */
export const formatTime = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

export type DateFilters = {
  preset?: string;
  startDate?: string;
  endDate?: string;
};

export type HistoricalFilterValues = DateFilters & {
  stationId?: string;
  driverId?: string;
  channel?: string;
  platform?: string;
  mode?: string;
  fromHour?: string;
  toHour?: string;
};

export const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "week", label: "This week" },
  { value: "custom", label: "Custom range" },
];

/**
 * Turns the control values into the query the API expects. A custom range
 * sends dates; anything else sends its preset.
 */
export const toQueryParams = (filters: HistoricalFilterValues) => {
  const { preset, startDate, endDate, fromHour, toHour, ...rest } = filters;

  const dates =
    preset === "custom" && startDate && endDate
      ? { startDate, endDate }
      : { preset: preset || "today" };

  const hours =
    fromHour !== undefined &&
    fromHour !== "" &&
    toHour !== undefined &&
    toHour !== ""
      ? { fromHour, toHour }
      : {};

  return { ...dates, ...hours, ...rest };
};
